import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";

const api = new API();

/*
|--------------------------------------------------------------------------
| INITIAL SETTINGS
|--------------------------------------------------------------------------
*/

const initialSettings = {
  businessName: "",
  legalName: "",

  mobile: "",
  alternateMobile: "",
  email: "",
  website: "",

  gstin: "",
  pan: "",

  address: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  district: "",
  state: "",
  stateCode: "",
  pincode: "",
  country: "India",

  currency: "INR",
  currencySymbol: "₹",
  decimalPlaces: 2,

  defaultPriceMode:
    "TAX_EXCLUSIVE",

  defaultSalesChannel:
    "STORE",

  defaultPaymentMethod:
    "CASH",

  defaultDueDays: 0,

  enableGst: true,
  enableCess: false,
  enableRoundOff: true,

  roundOffMethod:
    "NEAREST",

  invoiceTitle:
    "Tax Invoice",

  quotationTitle:
    "Quotation",

  proformaInvoiceTitle:
    "Proforma Invoice",

  saleOrderTitle:
    "Sale Order",

  deliveryChallanTitle:
    "Delivery Challan",

  paymentReceiptTitle:
    "Payment Receipt",

  saleReturnTitle:
    "Sale Return",

  termsAndConditions: "",
  invoiceNotes: "",
  footerText: "",

  logo: "",
  authorizedSignature: "",
  authorizedSignatoryName: "",

  themeColor: "#2563eb",

  invoiceTemplate:
    "CLASSIC",

  paperSize:
    "A4",

  printOrientation:
    "PORTRAIT",

  showLogo: true,
  showSignature: true,
  showBankDetails: true,
  showTermsAndConditions: true,
  showPaymentQrCode: false,

  autoConfirmOnlineOrders: false,
  autoCreateInvoiceFromOnlineOrder: true,
  autoReserveOnlineStock: true,

  allowNegativeStock: false,
  allowManualItems: true,
  allowInvoiceEditAfterConfirmation: false,

  enableSerialNumberTracking: false,
  enableBatchTracking: false,

  invoiceNumberReset:
    "FINANCIAL_YEAR",

  financialYearStartMonth: 4,

  bankDetails: [],

  paymentQrCode: {
    upiId: "",
    payeeName: "",
    qrImage: "",
  },

  emailSettings: {
    sendInvoiceAutomatically: false,
    ccEmail: "",
    replyToEmail: "",
    subjectTemplate: "",
    bodyTemplate: "",
  },

  whatsappSettings: {
    sendInvoiceAutomatically: false,
    messageTemplate: "",
  },

  isActive: true,
};

/*
|--------------------------------------------------------------------------
| INITIAL DROPDOWNS
|--------------------------------------------------------------------------
*/

const initialDropdowns = {
  currencies: [],
  priceModes: [],
  salesChannels: [],
  paymentMethods: [],
  roundOffMethods: [],
  invoiceTemplates: [],
  paperSizes: [],
  printOrientations: [],
  invoiceNumberResetOptions: [],
  financialYearStartMonths: [],
  countryOptions: [],
  stateOptions: [],
};

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
  settings: {
    ...initialSettings,
  },

  dropdowns: {
    ...initialDropdowns,
  },

  settingsLoading: false,
  dropdownsLoading: false,

  updateLoading: false,
  logoUploadLoading: false,
  signatureUploadLoading: false,

  error: null,
  actionError: null,

  operationSuccess: false,
  operationMessage: "",
};

/*
|--------------------------------------------------------------------------
| RESPONSE HELPERS
|--------------------------------------------------------------------------
*/

const getResponseData = (
  response,
  fallback = {}
) => {
  if (!response) {
    return fallback;
  }

  return (
    response.data ||
    response.result ||
    response
  );
};

const getErrorMessage = (
  response,
  fallbackMessage
) => {
  return (
    response?.message ||
    response?.error?.message ||
    response?.error ||
    fallbackMessage
  );
};

const rejectIfFailed = (
  response,
  rejectWithValue,
  fallbackMessage
) => {
  if (
    !response ||
    response.success === false ||
    response.status === "error"
  ) {
    return rejectWithValue(
      getErrorMessage(
        response,
        fallbackMessage
      )
    );
  }

  return null;
};

const extractSettings = (
  payload
) => {
  return (
    payload?.settings ||
    payload?.billingSettings ||
    payload
  );
};

/*
|--------------------------------------------------------------------------
| FETCH SETTINGS
|--------------------------------------------------------------------------
*/

export const fetchBillingSettings =
  createAsyncThunk(
    "billingSettings/fetchBillingSettings",
    async (
      _,
      {
        rejectWithValue,
      }
    ) => {
      const response =
        await api.get(
          config.getBillingSettings
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch billing settings."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH DROPDOWNS
|--------------------------------------------------------------------------
*/

export const fetchBillingSettingsDropdowns =
  createAsyncThunk(
    "billingSettings/fetchBillingSettingsDropdowns",
    async (
      _,
      {
        rejectWithValue,
      }
    ) => {
      const response =
        await api.get(
          config.getBillingSettingsDropdowns
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch billing settings dropdowns."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| UPDATE SETTINGS
|--------------------------------------------------------------------------
*/

export const updateBillingSettings =
  createAsyncThunk(
    "billingSettings/updateBillingSettings",
    async (
      payload,
      {
        rejectWithValue,
      }
    ) => {
      const response =
        await api.put(
          config.updateBillingSettings,
          payload || {}
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to update billing settings."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Billing settings updated successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| UPLOAD LOGO
|--------------------------------------------------------------------------
*/

export const uploadBillingLogo =
  createAsyncThunk(
    "billingSettings/uploadBillingLogo",
    async (
      file,
      {
        rejectWithValue,
      }
    ) => {
      if (!file) {
        return rejectWithValue(
          "Please select a logo file."
        );
      }

      const formData =
        new FormData();

      formData.append(
        "logo",
        file
      );

      const response =
        await api.postFormData(
          config.uploadBillingLogo,
          formData
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to upload billing logo."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Billing logo uploaded successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| UPLOAD SIGNATURE
|--------------------------------------------------------------------------
*/

export const uploadBillingSignature =
  createAsyncThunk(
    "billingSettings/uploadBillingSignature",
    async (
      file,
      {
        rejectWithValue,
      }
    ) => {
      if (!file) {
        return rejectWithValue(
          "Please select a signature file."
        );
      }

      const formData =
        new FormData();

      formData.append(
        "signature",
        file
      );

      const response =
        await api.postFormData(
          config.uploadBillingSignature,
          formData
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to upload billing signature."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Billing signature uploaded successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const billingSettingsSlice =
  createSlice({
    name:
      "billingSettings",

    initialState,

    reducers: {
      setBillingSettings: (
        state,
        action
      ) => {
        state.settings = {
          ...state.settings,
          ...(action.payload || {}),
        };
      },

      setBillingSettingField: (
        state,
        action
      ) => {
        const {
          key,
          value,
        } =
          action.payload || {};

        if (!key) {
          return;
        }

        state.settings[key] =
          value;
      },

      setBillingNestedSettingField: (
        state,
        action
      ) => {
        const {
          section,
          key,
          value,
        } =
          action.payload || {};

        if (
          !section ||
          !key
        ) {
          return;
        }

        if (
          !state.settings[
            section
          ] ||
          typeof state.settings[
            section
          ] !== "object"
        ) {
          state.settings[
            section
          ] = {};
        }

        state.settings[
          section
        ][key] =
          value;
      },

      setBillingBankDetails: (
        state,
        action
      ) => {
        state.settings.bankDetails =
          Array.isArray(
            action.payload
          )
            ? action.payload
            : [];
      },

      addBillingBankDetail: (
        state,
        action
      ) => {
        state.settings.bankDetails.push(
          action.payload || {
            bankName: "",
            branchName: "",
            accountHolderName: "",
            accountNumber: "",
            ifscCode: "",
            accountType: "",
            upiId: "",
            isDefault: false,
          }
        );
      },

      updateBillingBankDetail: (
        state,
        action
      ) => {
        const {
          index,
          changes,
        } =
          action.payload || {};

        if (
          index === undefined ||
          index < 0 ||
          index >=
            state.settings
              .bankDetails.length
        ) {
          return;
        }

        state.settings
          .bankDetails[
            index
          ] = {
          ...state.settings
            .bankDetails[
              index
            ],
          ...(changes || {}),
        };
      },

      removeBillingBankDetail: (
        state,
        action
      ) => {
        const index =
          Number(
            action.payload
          );

        if (
          Number.isNaN(
            index
          ) ||
          index < 0 ||
          index >=
            state.settings
              .bankDetails.length
        ) {
          return;
        }

        state.settings
          .bankDetails.splice(
            index,
            1
          );
      },

      setDefaultBillingBankDetail: (
        state,
        action
      ) => {
        const selectedIndex =
          Number(
            action.payload
          );

        state.settings.bankDetails =
          state.settings
            .bankDetails.map(
              (
                bank,
                index
              ) => ({
                ...bank,
                isDefault:
                  index ===
                  selectedIndex,
              })
            );
      },

      clearBillingSettingsErrors: (
        state
      ) => {
        state.error = null;
        state.actionError = null;
      },

      clearBillingSettingsOperation: (
        state
      ) => {
        state.operationSuccess =
          false;

        state.operationMessage =
          "";

        state.actionError =
          null;
      },

      resetBillingSettingsForm: (
        state
      ) => {
        state.settings = {
          ...initialSettings,
        };
      },

      resetBillingSettingsState:
        () => ({
          ...initialState,

          settings: {
            ...initialSettings,
          },

          dropdowns: {
            ...initialDropdowns,
          },
        }),
    },

    extraReducers: (
      builder
    ) => {
      /*
      |--------------------------------------------------------------------------
      | FETCH SETTINGS
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingSettings.pending,
          (state) => {
            state.settingsLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingSettings.fulfilled,
          (
            state,
            action
          ) => {
            state.settingsLoading =
              false;

            const settings =
              extractSettings(
                action.payload
              );

            state.settings = {
              ...initialSettings,
              ...(settings ||
                {}),

              paymentQrCode: {
                ...initialSettings
                  .paymentQrCode,
                ...(
                  settings
                    ?.paymentQrCode ||
                  {}
                ),
              },

              emailSettings: {
                ...initialSettings
                  .emailSettings,
                ...(
                  settings
                    ?.emailSettings ||
                  {}
                ),
              },

              whatsappSettings: {
                ...initialSettings
                  .whatsappSettings,
                ...(
                  settings
                    ?.whatsappSettings ||
                  {}
                ),
              },

              bankDetails:
                Array.isArray(
                  settings
                    ?.bankDetails
                )
                  ? settings
                      .bankDetails
                  : [],
            };
          }
        )
        .addCase(
          fetchBillingSettings.rejected,
          (
            state,
            action
          ) => {
            state.settingsLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch billing settings.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | DROPDOWNS
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingSettingsDropdowns.pending,
          (state) => {
            state.dropdownsLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingSettingsDropdowns.fulfilled,
          (
            state,
            action
          ) => {
            state.dropdownsLoading =
              false;

            const payload =
              action.payload || {};

            state.dropdowns = {
              ...initialDropdowns,
              ...(payload.dropdowns ||
                payload),
            };
          }
        )
        .addCase(
          fetchBillingSettingsDropdowns.rejected,
          (
            state,
            action
          ) => {
            state.dropdownsLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch billing settings dropdowns.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | UPDATE SETTINGS
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          updateBillingSettings.pending,
          (state) => {
            state.updateLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;

            state.operationMessage =
              "";
          }
        )
        .addCase(
          updateBillingSettings.fulfilled,
          (
            state,
            action
          ) => {
            state.updateLoading =
              false;

            const settings =
              extractSettings(
                action.payload
                  ?.data
              );

            state.settings = {
              ...state.settings,
              ...(settings ||
                {}),

              paymentQrCode: {
                ...state.settings
                  .paymentQrCode,
                ...(
                  settings
                    ?.paymentQrCode ||
                  {}
                ),
              },

              emailSettings: {
                ...state.settings
                  .emailSettings,
                ...(
                  settings
                    ?.emailSettings ||
                  {}
                ),
              },

              whatsappSettings: {
                ...state.settings
                  .whatsappSettings,
                ...(
                  settings
                    ?.whatsappSettings ||
                  {}
                ),
              },
            };

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload
                ?.message ||
              "Billing settings updated successfully.";
          }
        )
        .addCase(
          updateBillingSettings.rejected,
          (
            state,
            action
          ) => {
            state.updateLoading =
              false;

            state.actionError =
              action.payload ||
              action.error?.message ||
              "Unable to update billing settings.";

            state.operationSuccess =
              false;
          }
        );

      /*
      |--------------------------------------------------------------------------
      | LOGO UPLOAD
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          uploadBillingLogo.pending,
          (state) => {
            state.logoUploadLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          uploadBillingLogo.fulfilled,
          (
            state,
            action
          ) => {
            state.logoUploadLoading =
              false;

            const payload =
              action.payload
                ?.data ||
              {};

            state.settings.logo =
              payload.logo ||
              payload.logoUrl ||
              payload.fileUrl ||
              "";

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload
                ?.message ||
              "Billing logo uploaded successfully.";
          }
        )
        .addCase(
          uploadBillingLogo.rejected,
          (
            state,
            action
          ) => {
            state.logoUploadLoading =
              false;

            state.actionError =
              action.payload ||
              action.error?.message ||
              "Unable to upload billing logo.";

            state.operationSuccess =
              false;
          }
        );

      /*
      |--------------------------------------------------------------------------
      | SIGNATURE UPLOAD
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          uploadBillingSignature.pending,
          (state) => {
            state.signatureUploadLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          uploadBillingSignature.fulfilled,
          (
            state,
            action
          ) => {
            state.signatureUploadLoading =
              false;

            const payload =
              action.payload
                ?.data ||
              {};

            state.settings
              .authorizedSignature =
              payload.signature ||
              payload.signatureUrl ||
              payload.fileUrl ||
              "";

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload
                ?.message ||
              "Billing signature uploaded successfully.";
          }
        )
        .addCase(
          uploadBillingSignature.rejected,
          (
            state,
            action
          ) => {
            state.signatureUploadLoading =
              false;

            state.actionError =
              action.payload ||
              action.error?.message ||
              "Unable to upload billing signature.";

            state.operationSuccess =
              false;
          }
        );
    },
  });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
  setBillingSettings,
  setBillingSettingField,
  setBillingNestedSettingField,

  setBillingBankDetails,
  addBillingBankDetail,
  updateBillingBankDetail,
  removeBillingBankDetail,
  setDefaultBillingBankDetail,

  clearBillingSettingsErrors,
  clearBillingSettingsOperation,

  resetBillingSettingsForm,
  resetBillingSettingsState,
} = billingSettingsSlice.actions;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const billingSettingsSelector = (
  state
) => state.billingSettings;

export const billingSettingsDataSelector = (
  state
) =>
  state.billingSettings.settings;

export const billingSettingsDropdownsSelector = (
  state
) =>
  state.billingSettings.dropdowns;

export const billingBankDetailsSelector = (
  state
) =>
  state.billingSettings
    .settings
    .bankDetails;

export const billingPaymentQrSelector = (
  state
) =>
  state.billingSettings
    .settings
    .paymentQrCode;

export const billingEmailSettingsSelector = (
  state
) =>
  state.billingSettings
    .settings
    .emailSettings;

export const billingWhatsappSettingsSelector = (
  state
) =>
  state.billingSettings
    .settings
    .whatsappSettings;

export const billingSettingsLoadingSelector = (
  state
) => ({
    settingsLoading:
      state.billingSettings
        .settingsLoading,

    dropdownsLoading:
      state.billingSettings
        .dropdownsLoading,

    updateLoading:
      state.billingSettings
        .updateLoading,

    logoUploadLoading:
      state.billingSettings
        .logoUploadLoading,

    signatureUploadLoading:
      state.billingSettings
        .signatureUploadLoading,
  });

export const billingSettingsOperationSelector = (
  state
) => ({
    success:
      state.billingSettings
        .operationSuccess,

    message:
      state.billingSettings
        .operationMessage,

    error:
      state.billingSettings
        .actionError,
  });

export default billingSettingsSlice.reducer;